import {
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TableType } from '@common/ts/enums';
import { useUrlTable } from '@/hooks/useUrlTable';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LangContext } from '@/context/LangProvider';
import { ProjectBrandingColumns } from './ProjectBrandingColumns';
import CreateProjectBrandingModal from '../Modals/CreateProjectBrandingModal';
import FullPageLoader from '../atoms/Loader/FullPageLoader';
import DataTablePagination from '../paginations/DataTablePagination';
import { useGetThemes } from '@/reactQuery/get';
import { getPageCount } from '@/utils/getPageCount';
import { useDebouncedFilter } from '@/hooks/useDebouncedFilter';
import { sortingStateToSortConfig } from '@/utils/sortingStateToSortConfig';

const PAGE_SIZE = 20;

export default function ProjectBrandingTable() {
  const {
    urlCurrentPage,
    updateUrlPage,
    urlSorting,
    updateUrlSorting,
    updateUrlFilter,
    getUrlFilterValue,
  } = useUrlTable({ type: TableType.BRANDINGS });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const { lang } = useContext(LangContext);
  const [openModal, setOpenModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization_id: organizationId } = useParams();

  const nameFilter = getUrlFilterValue('name');
  const filters = useMemo(() => (nameFilter ? { name: nameFilter } : undefined), [nameFilter]);
  const sortConfig = useMemo(() => sortingStateToSortConfig(urlSorting), [urlSorting]);

  const { data: { items: themes, pagination } = {}, isLoading } = useGetThemes(
    organizationId,
    {
      page: urlCurrentPage,
      limit: PAGE_SIZE,
    },
    filters,
    sortConfig,
  );

  // Debounced filter for email search
  const { inputValue: nameInputValue, setInputValue: setNameInputValue } = useDebouncedFilter({
    filterKey: 'name',
    currentValue: nameFilter,
    onFilterChange: updateUrlFilter,
  });

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const handleOpenEditModal = (id: string) => {
    addQueryParams({ mode: 'edit', id });
    setOpenModal(true);
  };

  const columns = ProjectBrandingColumns(handleOpenEditModal);

  const totalCount = pagination?.totalItems || 0;

  const table = useReactTable({
    data: themes ?? [],
    columns,
    pageCount: getPageCount(totalCount, PAGE_SIZE),
    onSortingChange: updateUrlSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    state: {
      sorting: urlSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: urlCurrentPage - 1,
        pageSize: PAGE_SIZE,
      },
    },
  });

  useEffect(() => {
    setColumnFilters(nameFilter ? [{ id: 'name', value: nameFilter }] : []);
  }, [getUrlFilterValue]);

  if (isLoading || !themes) return <FullPageLoader />;

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-end w-full gap-4 pt-4 pb-2">
        <div className="flex items-center w-full gap-4 mb-2">
          {/* button for creating new branding */}
          <CreateProjectBrandingModal openModal={openModal} setOpenModal={setOpenModal} />
          <Input
            placeholder={lang.get('msg.filterBrandingByName')}
            value={nameInputValue}
            onChange={(event) => {
              updateUrlPage(1);
              setNameInputValue(event.target.value);
            }}
            className="w-full max-w-[400px]"
          />
        </div>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  style={{
                    backgroundColor: row?.original?.name === 'Default' ? '#e5e7ebb9' : '#FFFFFF',
                  }}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="last:justify-end last:flex" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {lang.get('msg.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        table={table}
        currentPage={urlCurrentPage}
        onPageChange={updateUrlPage}
      />
    </div>
  );
}
